/**
 * Single source of truth for document-ingestion accepted file types and the
 * rule that excludes Excel (.xlsx/.xls) from the allow list unless the Minerva
 * knowledge base is enabled for the team.
 *
 * Excel is a valid ingestion type ONLY when the team is in the Minerva flow
 * (`MINERVA_KB` in enabledFeatures). Every ingestion surface derives its accept
 * list and its post-selection guard from the helpers here so the strip-Excel
 * logic is never re-implemented per component.
 */

/** Excel workbook extensions, lower-case and dot-prefixed. */
const EXCEL_EXTENSIONS: ReadonlySet<string> = new Set([".xlsx", ".xls"]);

/** Answers KB / Minerva accept list (includes Excel; Excel stripped when off). */
export const ACCEPTED_FILE_TYPES =
  ".txt, .pdf, .docx, .doc, .html, .htm, .ppt, .pptx, .png, .jpg, .jpeg, .csv, .tsv, .xlsx";

/** Every extension in ACCEPTED_FILE_TYPES, normalized for membership checks. */
export const ANSWERS_KB_SUPPORTED_EXTENSIONS: ReadonlySet<string> = new Set(
  ACCEPTED_FILE_TYPES.split(",").map((e) => e.trim().toLowerCase()),
);

/** Normalize a raw extension (with or without leading dot) to `.ext` lower-case. */
const normalizeExtension = (ext: string): string =>
  ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`;

/** True when the extension is an Excel workbook (.xlsx/.xls), case-insensitive. */
export const isExcelExtension = (ext?: string): boolean =>
  !!ext && EXCEL_EXTENSIONS.has(normalizeExtension(ext));

/**
 * Remove Excel extensions from a comma-separated accept-list string when the
 * Minerva KB is NOT enabled. Returns the list unchanged when Minerva is on.
 * The single strip-Excel implementation shared by all ingestion surfaces.
 */
const excludeExcelWhenNotMinerva = (
  acceptList: string,
  isMinervaKbEnabled: boolean,
): string => {
  if (isMinervaKbEnabled) return acceptList;
  return acceptList
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e.length > 0 && !EXCEL_EXTENSIONS.has(e.toLowerCase()))
    .join(", ");
};

/**
 * Answers KB / Minerva accept list. Excel is included only when the Minerva
 * knowledge base is enabled; otherwise it is stripped so the browse dialog and
 * drop zone do not advertise Excel as supported.
 */
export const getAcceptedFileTypes = (isMinervaKbEnabled: boolean): string =>
  excludeExcelWhenNotMinerva(ACCEPTED_FILE_TYPES, isMinervaKbEnabled);

/**
 * Whether an extension is a supported Answers KB ingestion type. Excel
 * extensions are unsupported unless the Minerva KB is enabled. Defaults to
 * Minerva-enabled behavior so existing single-argument callers keep the
 * pre-Minerva-aware (Excel-supported) semantics.
 */
export const isAnswersKbSupportedExtension = (
  ext?: string,
  isMinervaKbEnabled = true,
): boolean => {
  if (!ext) return false;
  const normalized = normalizeExtension(ext);
  // Excel is only a supported ingestion type when the Minerva KB is enabled.
  if (!isMinervaKbEnabled && EXCEL_EXTENSIONS.has(normalized)) return false;
  return ANSWERS_KB_SUPPORTED_EXTENSIONS.has(normalized);
};

/**
 * Whether a file name matches a comma-separated accept list of extensions
 * (e.g. ".pdf" or ".pdf, .png"), case-insensitive. An empty or missing list
 * accepts everything. Needed because the file upload drop zone does not apply
 * `accept`; only the browse dialog does.
 */
export const isFileNameAccepted = (
  fileName: string,
  acceptList?: string,
): boolean => {
  const extensions = (acceptList ?? "")
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e.length > 0)
    .map(normalizeExtension);
  if (extensions.length === 0) return true;
  const name = fileName.toLowerCase();
  return extensions.some((ext) => name.endsWith(ext));
};
